// Redux duck for ListingWizardPage
import { storableError } from '../../util/errors';
import { UploadImage, UploadVideo } from '../../util/cloudinary';
import { v4 as uuid4 } from 'uuid';
import { createAlgoliaData, enhanceField, generateAIImages, generateAIListing, getCredits, getUserAchievements, userActiveListings } from '../../util/api';
import { generateAchievementReviews } from '../../util/reviews';
import { base64ToFile, denormalisedResponseEntities, getEnhancedContent } from '../../util/data';
import { fetchExchangeRates } from '../../ducks/exchangeRates.duck';
import moment from 'moment';
import { LISTING_ALOGLIA_REMOVABLE_FIELDS, LISTING_USER_PUBLIC_DATA_REMOVABLE_FIELDS } from '../../util/constants';
import { omit } from 'lodash';

const filterPublicDataToAlgolia = (publicData = {}, removeables = []) => {
    const newObj = {};
    for (let key in publicData) {
      if (!removeables.includes(key)) {
        newObj[key] = publicData[key];
      }
    }
    return newObj;
  };
  

// Action types
const SAVE_INPUT = 'ListingWizardPage/SAVE_INPUT';
const UPLOAD_IMAGES_REQUEST = 'ListingWizardPage/UPLOAD_IMAGES_REQUEST';
const UPLOAD_IMAGES_SUCCESS = 'ListingWizardPage/UPLOAD_IMAGES_SUCCESS';
const UPLOAD_IMAGES_ERROR = 'ListingWizardPage/UPLOAD_IMAGES_ERROR';
const GENERATE_LISTING_REQUEST = 'ListingWizardPage/GENERATE_LISTING_REQUEST';
const GENERATE_LISTING_SUCCESS = 'ListingWizardPage/GENERATE_LISTING_SUCCESS';
const GENERATE_LISTING_ERROR = 'ListingWizardPage/GENERATE_LISTING_ERROR';
const RESET_STATE = 'ListingWizardPage/RESET_STATE';
// About Owner section
const FETCH_OWNER_INFO_REQUEST = 'ListingWizardPage/FETCH_OWNER_INFO_REQUEST';
const FETCH_OWNER_INFO_SUCCESS = 'ListingWizardPage/FETCH_OWNER_INFO_SUCCESS';
const FETCH_OWNER_INFO_ERROR = 'ListingWizardPage/FETCH_OWNER_INFO_ERROR';

// Save Draft
const SAVE_LISTING_DRAFT_REQUEST = 'ListingWizardPage/SAVE_LISTING_DRAFT_REQUEST';
const SAVE_LISTING_DRAFT_SUCCESS = 'ListingWizardPage/SAVE_LISTING_DRAFT_SUCCESS';
const SAVE_LISTING_DRAFT_ERROR = 'ListingWizardPage/SAVE_LISTING_DRAFT_ERROR';

const CREATE_LISTING_REQUEST = 'ListingWizardPage/CREATE_LISTING_REQUEST';
const CREATE_LISTING_SUCCESS = 'ListingWizardPage/CREATE_LISTING_SUCCESS';
const CREATE_LISTING_ERROR = 'ListingWizardPage/CREATE_LISTING_ERROR';

const GET_CREDITS_REQUEST = 'ListingWizardPage/GET_CREDITS_REQUEST';
const GET_CREDITS_SUCCESS = 'ListingWizardPage/GET_CREDITS_SUCCESS';
const GET_CREDITS_ERROR = 'ListingWizardPage/GET_CREDITS_ERROR';

const ENHANCE_FIELD_REQUEST = 'ListingWizardPage/ENHANCE_FIELD_REQUEST';
const ENHANCE_FIELD_SUCCESS = 'ListingWizardPage/ENHANCE_FIELD_SUCCESS';
const ENHANCE_FIELD_ERROR = 'ListingWizardPage/ENHANCE_FIELD_ERROR';

const GENERATING_AI_IMAGES_REQUEST = 'ListingWizardPage/GENERATING_AI_IMAGES_REQUEST';
const GENERATING_AI_IMAGES_SUCCESS = 'ListingWizardPage/GENERATING_AI_IMAGES_SUCCESS';
const GENERATING_AI_IMAGES_ERROR = 'ListingWizardPage/GENERATING_AI_IMAGES_ERROR';

// Initial state
const initialState = {
    input: null,
    imagesUploading: false,
    imagesError: null,
    images: [],
    sharetribeImageIds: [],
    generating: false,
    generateError: null,
    generatedListing: {},
    listingToken: null,
    retryCount: 0,
    lastError: null,
    ownerInfo: null,
    saveListingDraftInProgress: false,
    saveListingDraftError: null,
    createdListing: null,
    createListingError: null,
    createListingInProgress: false,
    credits: {},
    creditsError: null,
    creditsLoading: false,

    enhanceFieldInProgress: {},
    enhanceFieldError: {},

    generatedImages: [],
    generateImagesError: null,
    generateImagesInProgress: false,
};



// Reducer
export default function reducer(state = initialState, action = {}) {
    switch (action.type) {
        case SAVE_INPUT:
            return { ...state, input: action.payload };
        case UPLOAD_IMAGES_REQUEST:
            return {
                ...state,
                imagesUploading: true,
                imagesError: null,
                lastError: null
            };
        case UPLOAD_IMAGES_SUCCESS:
            return {
                ...state,
                imagesUploading: false,
                images: [...state.images, ...action.payload.images],
                sharetribeImageIds: [...state.sharetribeImageIds, ...action.payload.sharetribeImageIds],
                retryCount: 0,
                lastError: null
            };
        case UPLOAD_IMAGES_ERROR:
            return {
                ...state,
                imagesUploading: false,
                imagesError: action.payload,
                lastError: action.payload,
                retryCount: state.retryCount + 1
            };
        case GENERATE_LISTING_REQUEST:
            return {
                ...state,
                generating: true,
                generateError: null,
                lastError: null
            };
        case GENERATE_LISTING_SUCCESS:
            return {
                ...state,
                generating: false,
                generatedListing: action.payload.content,
                listingToken: action.payload.listingToken,
                retryCount: 0,
                lastError: null
            };
        case GENERATE_LISTING_ERROR:
            return {
                ...state,
                generating: false,
                generateError: action.payload,
                lastError: action.payload,
                retryCount: state.retryCount + 1
            };
        case RESET_STATE:
            return initialState;
        case FETCH_OWNER_INFO_REQUEST:
            return { ...state, ownerInfoLoading: true, ownerInfoError: null };
        case FETCH_OWNER_INFO_SUCCESS:
            return {
                ...state,
                ownerInfo: action.payload,
                ownerInfoLoading: false,
            };
        case FETCH_OWNER_INFO_ERROR:
            return { ...state, ownerInfoLoading: false, ownerInfoError: action.payload };
        case SAVE_LISTING_DRAFT_REQUEST:
            return { ...state, saveListingDraftInProgress: true, saveListingDraftError: null };
        case SAVE_LISTING_DRAFT_SUCCESS:
            return { ...state, saveListingDraftInProgress: false };
        case SAVE_LISTING_DRAFT_ERROR:
            return { ...state, saveListingDraftInProgress: false, saveListingDraftError: action.payload };
        case CREATE_LISTING_REQUEST:
            return { ...state, createListingInProgress: true, createListingError: null };
        case CREATE_LISTING_SUCCESS:
            return { ...state, createListingInProgress: false, createdListing: action.payload };
        case CREATE_LISTING_ERROR:
            return { ...state, createListingInProgress: false, createListingError: action.payload };
        case GET_CREDITS_REQUEST:
            return { ...state, creditsLoading: true, creditsError: null };
        case GET_CREDITS_SUCCESS:
            return { ...state, creditsLoading: false, credits: action.payload };
        case GET_CREDITS_ERROR:
            return { ...state, creditsLoading: false, creditsError: action.payload };

        case ENHANCE_FIELD_REQUEST:
            return {
                ...state,
                enhanceFieldInProgress: {
                    ...state.enhanceFieldInProgress,
                    [action.payload.field]: true,
                },
            };
        case ENHANCE_FIELD_SUCCESS:
            return {
                ...state,
                enhanceFieldInProgress: {
                    ...state.enhanceFieldInProgress,
                    [action.payload.field]: false,
                },
                generatedListing: {
                    ...state.generatedListing,
                    [action.payload.field]: getEnhancedContent(action.payload.field, action.payload.enhancedContent, state.generatedListing?.online_review_summary?.rating),
                },
            };
        case ENHANCE_FIELD_ERROR:
            return {
                ...state,
                enhanceFieldInProgress: {
                    ...state.enhanceFieldInProgress,
                    [action.payload.field]: false,
                },
                enhanceFieldError: {
                    ...state.enhanceFieldError,
                    [action.payload.field]: action.payload.error,
                },
            };

        case GENERATING_AI_IMAGES_REQUEST:
            return {
                ...state,
                generateImagesInProgress: true,
                generateImagesError: null,
            };
        case GENERATING_AI_IMAGES_SUCCESS:
            return {
                ...state,
                generateImagesInProgress: false,
                generatedImages: [...state.generatedImages, ...action.payload],
            };
        case GENERATING_AI_IMAGES_ERROR:
            return {
                ...state,
                generateImagesInProgress: false,
                generateImagesError: action.payload,
            };
        default:
            return state;
    }
}

// Action creators
export const saveInput = payload => ({ type: SAVE_INPUT, payload });
export const uploadImagesRequest = () => ({ type: UPLOAD_IMAGES_REQUEST });
export const uploadImagesSuccess = payload => ({ type: UPLOAD_IMAGES_SUCCESS, payload });
export const uploadImagesError = error => ({ type: UPLOAD_IMAGES_ERROR, payload: storableError(error) });
export const generateListingRequest = () => ({ type: GENERATE_LISTING_REQUEST });
export const generateListingSuccess = payload => ({ type: GENERATE_LISTING_SUCCESS, payload });
export const generateListingError = error => ({ type: GENERATE_LISTING_ERROR, payload: storableError(error) });
export const resetState = () => ({ type: RESET_STATE });
export const fetchOwnerInfoRequest = () => ({ type: FETCH_OWNER_INFO_REQUEST });
export const fetchOwnerInfoSuccess = payload => ({ type: FETCH_OWNER_INFO_SUCCESS, payload });
export const fetchOwnerInfoError = error => ({ type: FETCH_OWNER_INFO_ERROR, payload: storableError(error) });
export const saveListingDraftRequest = () => ({ type: SAVE_LISTING_DRAFT_REQUEST });
export const saveListingDraftSuccess = payload => ({ type: SAVE_LISTING_DRAFT_SUCCESS, payload });
export const saveListingDraftError = error => ({ type: SAVE_LISTING_DRAFT_ERROR, payload: storableError(error) });

export const createListingRequest = () => ({ type: CREATE_LISTING_REQUEST });
export const createListingSuccess = (data) => ({ type: CREATE_LISTING_SUCCESS, payload: data.data });
export const createListingError = (error) => ({ type: CREATE_LISTING_ERROR, payload: storableError(error) });

export const getCreditsRequest = () => ({ type: GET_CREDITS_REQUEST });
export const getCreditsSuccess = payload => ({ type: GET_CREDITS_SUCCESS, payload });
export const getCreditsError = error => ({ type: GET_CREDITS_ERROR, payload: storableError(error) });

export const enhanceFieldRequest = (field) => ({ type: ENHANCE_FIELD_REQUEST, payload: { field } });
export const enhanceFieldSuccess = (field, enhancedContent) => ({ type: ENHANCE_FIELD_SUCCESS, payload: { field, enhancedContent } });
export const enhanceFieldError = (field, error) => ({ type: ENHANCE_FIELD_ERROR, payload: { field, error: storableError(error) } });

export const generateAIImagesRequest = () => ({ type: GENERATING_AI_IMAGES_REQUEST });
export const generateAIImagesSuccess = (data) => ({ type: GENERATING_AI_IMAGES_SUCCESS, payload: data });
export const generateAIImagesError = (error) => ({ type: GENERATING_AI_IMAGES_ERROR, payload: storableError(error) });

// Constants
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

// Thunks
export const uploadImages = (images) => async (dispatch, getState, sdk) => {
    try {
        if (!images?.length) return;

        dispatch(uploadImagesRequest());

        const sharetribeImageIds = [];
        const uploadImage = async (file) => {
            // First uplpoad to sharetribe
            const imageRes = await sdk.images.upload({ image: file });
            const id = imageRes.data.data.id.uuid;
            sharetribeImageIds.push(id);
            // Then upload to cloudinary
            const result = await UploadImage({ file, id });
            if (!result || !result.secure_url) {
                throw new Error('Image upload failed');
            }

            return { public_id: id, secure_url: result.secure_url, height: result.height, width: result.width, sharetribeImgId: id };
        };

        const uploadedImages = await Promise.all(
            images.map(file => uploadImage(file))
        );

        dispatch(uploadImagesSuccess({ images: uploadedImages, sharetribeImageIds }));
        return uploadedImages;
    } catch (error) {
        console.error('Failed to upload images:', error);
        dispatch(uploadImagesError(error));
    }
};

export const generateListing = input => async (dispatch, getState, sdk) => {
    try {
        dispatch(generateListingRequest());

        const { currentUserCurrency = 'USD' } = getState().user;

        let finalInputData = {
            productDescription: input.productDescription,
            location: input.location.selectedPlace.address,
            externalProductUrl: input.externalProductUrl,

            // Optional content flags
            includeHowToUse: input.includeHowToUse,
            includeSuggestedUseCases: input.includeSuggestedUseCases,
            includeRentalRates: input.includeRentalRates,
            includeReviewSummary: input.includeReviewSummary,
            includeLocationContext: input.includeLocationContext,
            includeFaqs: input.includeFaqs,
            includeAlternativeTitles: input.includeAlternativeTitles,

            // Suggestion based fields options
            itemCategoryOptions: input.itemCategoryOptions,
            itemConditionOptions: input.itemConditionOptions,
            itemReplacementValueOptions: input.itemReplacementValueOptions,
            currency: currentUserCurrency,
        };

        // Handle image uploads if present
        if (input.images && input.images.length) {
            try {
                const uploadedImages = await dispatch(uploadImages(input.images));
                finalInputData = { ...finalInputData, images: uploadedImages.map(img => img.secure_url) };
            } catch (error) {
                console.error('Failed to upload images:', error);
                // Continue with generation even if image upload fails
            }
        }


        const { data } = await generateAIListing(finalInputData);

        const { listingToken, content, images } = data;

        if (!listingToken || !content) {
            throw new Error('Invalid listing data received');
        }

        dispatch(generateListingSuccess({ listingToken, content }));

        if (images && images.length) {
            const files = images.map(img => {
                return {
                    secure_url: img.secure_url,
                    height: img.height,
                    width: img.width,
                    public_id: img.public_id,
                    file: base64ToFile(img.base64Image, `listing-img-${uuid4()}`)
                }
            });
            const uploadedImages = await Promise.all(files.map(async (file) => {
                const imgRes = await sdk.images.upload({ image: file.file });
                const sharetribeId = imgRes.data.data.id.uuid;
                return {
                    sharetribeImgId: sharetribeId,
                    secure_url: file.secure_url,
                    height: file.height,
                    width: file.width,
                    public_id: file.public_id
                }
            }));
            
            dispatch(uploadImagesSuccess({ images: uploadedImages, sharetribeImageIds: uploadedImages.map(img => img.sharetribeImgId) }));
        }

        return { listingToken, content, images };
    } catch (error) {
        dispatch(generateListingError(error));
        throw error;
    }
};

// Thunk to fetch current user's profile, listings count, reviews, rating, and verification
export const fetchOwnerInfo = () => async (dispatch, getState, sdk) => {
    try {
        dispatch(fetchOwnerInfoRequest());
        const state = getState();
        const { isAuthenticated } = state.auth;
        const currentUser = state.user.currentUser;

        if (!isAuthenticated || !currentUser || !currentUser.id) {
            throw new Error('User not authenticated');
        }

        const userId = currentUser.id.uuid;

        // Parallel API calls
        const [listingsRes, achievementsRes, reviewsRes] = await Promise.all([
            sdk.listings.query({ authorId: userId, perPage: 1 }),
            getUserAchievements(userId),
            sdk.reviews.query({
                subjectId: userId,
                type: 'ofProvider',
                state: 'public',
                include: ['author', 'author.profileImage'],
                'fields.image': ['variants.square-small', 'variants.square-small2x'],
            })
        ]);

        const totalListings = listingsRes.data.meta.totalItems;
        const { emailVerified, stripeConnected, publishedListings, displayName } = achievementsRes.data;
        const achievementReviews = generateAchievementReviews(
            emailVerified,
            stripeConnected,
            publishedListings,
            displayName
        );

        const reviews = denormalisedResponseEntities(reviewsRes);
        const authorReviews = [...reviews, ...achievementReviews];
        const rating = currentUser?.attributes?.profile?.publicData?.rating || null;

        dispatch(fetchOwnerInfoSuccess({
            totalListings: totalListings || 1,
            reviews: authorReviews,
            rating,
            verified: emailVerified,
            profile: currentUser,
        }));
    } catch (error) {
        dispatch(fetchOwnerInfoError(error));
    }
};


// Save Draft 
// Create listing in draft state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
export function requestSaveListingDraft(data, config) {
    return async (dispatch, getState, sdk) => {
        try {
            dispatch(saveListingDraftRequest());
            const draftResponse = await sdk.ownListings.createDraft(data);
            if (!draftResponse?.data?.data?.id) {
                throw new Error('Failed to create listing draft: No ID returned');
            }

            dispatch(saveListingDraftSuccess(draftResponse));
            dispatch(resetState())
            return draftResponse;
        } catch (error) {
            console.error('Failed to create listing draft:', error, {
                listingData: {
                    ...data,
                    publicData: data.publicData ? '[...large data omitted...]' : null
                }
            });
            dispatch(saveListingDraftError(storableError(error)));
            throw error;
        }
    };
};

export function requestCreateListing(data, config) {
    return async (dispatch, getState, sdk) => {
        dispatch(createListingRequest());

        try {
            const {
                imagesToUpload = [],
                base64Images = [],
                availabilityExceptions = [],
                ...rest
            } = data;

            let imageIds = [];
            let listingImagesMeta = [];

            // Upload file-based images
            const fileImages = imagesToUpload.filter(
                img => img instanceof File
            );

            if (fileImages.length) {
                await dispatch(uploadImages(fileImages));
            };

            const { images, sharetribeImageIds } = getState().ListingWizardPage;
            imageIds = [...sharetribeImageIds];
            listingImagesMeta = [...images];

            // Upload base64 images
            if (base64Images.length) {
                const newListingImages = await Promise.all(
                    base64Images.map(async img => {
                        const file = base64ToFile(img.base64Image, `listing-img-${uuid4()}`);
                        const res = await sdk.images.upload({ image: file });
                        return {
                            sharetribeImgId: res?.data?.data?.id?.uuid,
                            secure_url: img.secure_url,
                            public_id: img.public_id,
                            height: img.height,
                            width: img.width,
                        };
                    })
                );

                const sharetribeImageIds = newListingImages.map(img => img.sharetribeImgId);
                imageIds = [...imageIds, ...sharetribeImageIds];
                listingImagesMeta = [...listingImagesMeta, ...newListingImages];
            }

            // Upload instruction video if it's a file
            const publicData = { ...(rest.publicData || {}) };

            if (publicData?.instructions?.video instanceof File) {
                try {
                    const videoFile = publicData.instructions.video;
                    const uploadRes = await UploadVideo({
                        file: videoFile,
                        id: uuid4(),
                    });

                    publicData.instructions.video = {
                        public_id: uploadRes.public_id,
                        asset_id: uploadRes.asset_id,
                        width: uploadRes.width,
                        height: uploadRes.height,
                        playback_url: uploadRes.playback_url,
                    };
                } catch (videoErr) {
                    console.error('Video upload failed:', videoErr);
                }
            }

            // Attach image metadata
            publicData.listingImages = listingImagesMeta;
            if (base64Images.length) {
                publicData.generatedImages = base64Images.map(img => ({
                    secure_url: img.secure_url,
                    public_id: img.public_id,
                    height: img.height,
                    width: img.width,
                }));
            }

            // Prepare final listing payload
            const listingPayload = {
                ...rest,
                images: imageIds,
                publicData,
            };

            // Create listing
            const createRes = await sdk.ownListings.create(listingPayload, {
                expand: true,
            });

            const newListingId = createRes?.data?.data?.id;
            if (!newListingId) {
                throw new Error('Failed to create listing draft: No ID returned');
            }

            // Add availability exceptions
            // filter the exceptions to remove the empty ones
            const filteredAvailabilityExceptions = availabilityExceptions?.filter(exception => exception.start && exception.end);
            if (filteredAvailabilityExceptions.length) {
                await Promise.all(
                    filteredAvailabilityExceptions.map(exception =>
                        sdk.availabilityExceptions.create({
                            listingId: newListingId,
                            start: new Date(exception.start),
                            end: new Date(exception.end),
                            seats: exception.seats,
                        })
                    )
                );
            };
            dispatch(createListingSuccess(createRes));
            dispatch(resetState());

            // Save to Algolia and update current user
            const { currentUser } = getState().user;
            const currentUserCreatedAt = currentUser.attributes.createdAt;
            const {
                    firstName,
                    lastName,
                    displayName,
                    publicData: userPublicData = {},
                    email,
                  } = currentUser.attributes.profile;
            
                  const listing = createRes.data.data || {};
                  const attr = listing.attributes || {};
                  const listingId = listing.id;

                  if (attr.state == 'published') {
                    // We have to know the position of listing
                    // to define its default initial order.
                    // It will be "total listings"
                    sdk.ownListings.query()
                    .then(({ data }) => {
                      const { meta } = data || {};
                      const totalItems = meta?.totalItems;
                      const defaultOrder = totalItems ? totalItems : 0;
            
                      // Update in algolia
                      createAlgoliaData({
                        indexName: process.env.REACT_APP_ALGOLIA_LISTINGS_INDEX,
                        objectID: listingId.uuid,
                        state: attr.state,
                        _geoloc:
                          attr.geolocation && attr.geolocation.lat
                            ? { lat: attr.geolocation.lat, lng: attr.geolocation.lng }
                            : {},
                        title: attr.title,
                        description: attr.description.slice(0, 999),
                        price: attr.price,
                        publicData: {
                            ...filterPublicDataToAlgolia(attr.publicData, LISTING_ALOGLIA_REMOVABLE_FIELDS),
                        },
                        metadata: attr.metadata,
                        user: {
                          id: currentUser.id.uuid,
                          firstName,
                          lastName,
                          email,
                          displayName,
                          publicData: omit(userPublicData, LISTING_USER_PUBLIC_DATA_REMOVABLE_FIELDS),
                        },
                        createdAt: attr.createdAt,
                        timestamp: moment(attr.createdAt).unix(),
                        order: defaultOrder,
                      });
            
                      // Update in sharetribe
                      sdk.ownListings.update({
                        id: listingId.uuid,
                        publicData: {
                          order: defaultOrder,
                        },
                      });
                    });
            
                    // Create user record in Algolia when he first time
                    // publish his listing. Need to know the total active
                    // listings so his store can be ranked.
                    userActiveListings(currentUser.id.uuid)
                    .then(res => {
                      const { totalListings } = res.data || {};
            
                      // User
                      createAlgoliaData({
                        indexName: process.env.REACT_APP_ALGOLIA_USERS_INDEX,
                        objectID: currentUser.id.uuid,
                        _geoloc:
                          userPublicData.origin?.lng && userPublicData.origin?.lat
                            ? {
                              lat: userPublicData.origin.lat,
                              lng: userPublicData.origin.lng,
                            }
                            : {},
                        firstName,
                        lastName,
                        displayName,
                        email,
                        publicData: {
                          ...filterPublicDataToAlgolia(userPublicData, [
                            'profileImage',
                            'cart',
                          ]),
                          totalListings,
                        },
                        ...(userPublicData.profileImage
                          ? { profileImage: userPublicData.profileImage }
                          : {}),
                        createdAt: currentUserCreatedAt,
                        timestamp: moment(currentUserCreatedAt).unix(),
                      });
                    });
                  };
            return createRes.data.data;
        } catch (error) {
            console.error('Failed to create listing:', error, {
                listingDataSummary: {
                    keys: Object.keys(data),
                    publicDataKeys: data.publicData ? Object.keys(data.publicData) : null,
                },
            });
            dispatch(createListingError(error));
            throw error;
        }
    };
}


export const requestGetCredits = (listingToken) => async (dispatch, getState, sdk) => {
    try {
        dispatch(getCreditsRequest());
        const credits = await getCredits(listingToken);
        dispatch(getCreditsSuccess(credits.remainingCredits));
    } catch (error) {
        dispatch(getCreditsError(error));
    }
};

export const requestEnhanceField = (enhanceFieldParams) => async (dispatch, getState, sdk) => {
    try {
        dispatch(enhanceFieldRequest(enhanceFieldParams.fieldName));
        const { data } = await enhanceField(enhanceFieldParams);
        const remainingCredits = data.remainingCredits;

        dispatch(enhanceFieldSuccess(data.fieldName, data.enhancedContent));
        dispatch(getCreditsSuccess(remainingCredits));

        return {
            fieldName: data.fieldName,
            enhancedContent: data.enhancedContent
        };
    } catch (error) {
        dispatch(enhanceFieldError(enhanceFieldParams.fieldName, error));
        throw error;
    }
};

export const requestGenerateAIImages = ({ listingId, ...params }) => async (dispatch, getState, sdk) => {
    try {
        dispatch(generateAIImagesRequest());
        const { data } = await generateAIImages(params);
        const remainingCredits = data.remainingCredits;
        const images = data.images;
        const dataToSave = images.map(image => {
            return {
                secure_url: image.imageUrl,
                public_id: image.publicId,
                height: image.height,
                width: image.width
            }
        });
        dispatch(getCreditsSuccess(remainingCredits));
        dispatch(generateAIImagesSuccess(images));

        // Save generatedImages to sharetribe listing under "generatedImages", as we will not delete them
        // Do this asynchronously
        if (listingId) {
            sdk.ownListings.show({
                id: listingId
            }).then((res) => {
                const listing = res.data.data;
                const existingGeneratedImages = listing.attributes.publicData.generatedImages || [];
                const updatedGeneratedImages = [...existingGeneratedImages, ...dataToSave];
                return sdk.ownListings.update({
                    id: listingId,
                    publicData: {
                        generatedImages: updatedGeneratedImages
                    }
                });
            })
                .catch((error) => {
                    console.error('Failed to update listing:', error);
                });
        }
    } catch (error) {
        dispatch(generateAIImagesError(error));
    }
};

// Load data
export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
    try {
        const promises = [];
        promises.push(dispatch(fetchOwnerInfo()));

        if (params.listingToken) {
            promises.push(dispatch(requestGetCredits(params.listingToken)));
        }

        // Fetch exchange rates, do not fetch if already exists.
        const { exchangeRates } = getState().exchangeRate;

        if (typeof window != 'undefined' && exchangeRates && exchangeRates.length == 0) {
            promises.push(dispatch(fetchExchangeRates()));
        };

        await Promise.all(promises);
    } catch (error) {
        console.error('Error loading owner info:', error);
    }
};